/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Tabs, Tab } from 'material-ui/Tabs';
import RaisedButton from 'material-ui/RaisedButton';
import FlatButton from 'material-ui/FlatButton';
import { Typography, Box } from '@material-ui/core';
import { tabStyles } from '../../../theme/AllocateBaseTheme';
import AddressDetails from '../AddressDetails/AddressDetails';
import PersonaliseTreatment from '../PersonaliseTreatment/PersonaliseTreatment';

import TreatmentDetails from '../TreatmentDetails/TreatmentDetails';
import TreatmentSchedule, { selectionTypes } from '../TreatmentSchedule/TreatmentSchedule';
import globals from '../../../globals/communityGlobals';
import _ from '../../../utils/lodash';
import { refSetter } from '../../../utils/helpers';
import { recurrenceTypes } from '../../../settings/constants';
import PatientPropType from '../../../propTypes/entities/patient';
import { red500 } from '../../../theme/AllocateColors';

import timeSlotPropType from '../../../propTypes/entities/timeSlot';
import { getDateForApi } from '../../../utils/dates';
import ContactDetails from '../AddressDetails/ContactDetails';
import { ExternalProviderType } from '../../../enums';

import AuditTrail from '../../../components/common/AuditTrail/AuditTrail';
import { AuditTrailType } from '../../../enums/auditTrail';

const locale = globals.getArea('treatments');

const tabIndexToNameMap = {
  0: 'treatment',
  1: 'treatmentSchedule',
  2: 'addressContact',
  3: 'personaliseTreatment',
};

export const actionTypes = {
  edit: 'EDIT',
  create: 'CREATE',
  view: 'VIEW',
};

class TreatmentPreview extends React.Component {
  constructor(props) {
    super(props);
    const validityDefaults = props.actionType === actionTypes.edit ? true : null;
    this.state = {
      formsValidity: {
        addressContactIsValid: validityDefaults,
        treatmentScheduleIsValid: validityDefaults,
        treatmentIsValid: validityDefaults,
      },
      wholeFormErrorMessage: null,
      selectedTab: 0,
      isReset: false,
      alternativeAddressInput: this.props.treatmentAddress !== null,
      treatmentCareType: this.props.treatment ? this.props.treatment.visitType : 'OnSite',
      patientContact: {
        landlinePhoneNumber: this.props.patient ? this.props.patient.contact.homeTelephone : '',
        otherPhoneNumber: this.props.patient ? this.props.patient.contact.workTelephone : '',
        mobilePhoneNumber: this.props.patient ? this.props.patient.contact.mobileTelephone : '',
        isModified: false,
      },
      activitiesIds: this.props.treatmentDetails.activitiesIds,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.formsValidity !== this.state.formsValidity &&
      this.canSave() &&
      this.state.wholeFormErrorMessage !== null
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        wholeFormErrorMessage: null,
      });
    }
  }

  setRef = refSetter.bind(this);

  setValidity = formName => value => () => {
    this.setState({
      formsValidity: {
        ...this.state.formsValidity,
        [`${formName}IsValid`]: value,
      },
    });
  };

  getPatientPhoneNumbers = patientPhoneNumbers => {
    this.setState({ patientContact: patientPhoneNumbers });
  };

  hasAlternativeAddres = alternativeAddress => {
    this.setState({ alternativeAddressInput: alternativeAddress });
  };

  handleCareTypeChange = selectionType => {
    this.setState({ treatmentCareType: selectionType });
  };

  handleActivityChange = values => {
    this.setState({ activitiesIds: values });
  };

  handleSave = () => {
    if (!this.canSave()) {
      this[`${tabIndexToNameMap[0]}Form`].submit();
      this[`${tabIndexToNameMap[1]}Form`].submit();
      if (this.props.actionType === actionTypes.edit) {
        this[`${tabIndexToNameMap[3]}Form`].submit();
      }
      this.setState({
        wholeFormErrorMessage: locale.getMsg('pleaseCompleteTabs'),
        isReset: false,
      });
      return;
    }
    const { saveCallback, patient } = this.props;
    const isSpecific = this.treatmentScheduleWrapper.state.selectionType === selectionTypes.specific;
    const isMultiple = this.treatmentScheduleWrapper.state.selectionType === selectionTypes.multiple;
    const address =
      this.state.alternativeAddressInput && !_.isEmpty(this.addressContactForm)
        ? this.addressContactForm.getModel().alternativeAddress
        : null;
    const visitType = this.state.treatmentCareType;
    const overriddenRagStatusId =
      !_.isEmpty(this.personaliseTreatment) && !_.isEmpty(this.personaliseTreatment.state.selectedRag)
        ? this.personaliseTreatment.state.selectedRag.id
        : null;
    const overriddenBandId =
      !_.isEmpty(this.personaliseTreatment) && !_.isEmpty(this.personaliseTreatment.state.selectedBand)
        ? this.personaliseTreatment.state.selectedBand.id
        : null;
    const preferredClinicianId =
      !_.isEmpty(this.personaliseTreatment) && !_.isEmpty(this.personaliseTreatment.state.selectedPreferredClinicianId)
        ? this.personaliseTreatment.state.selectedPreferredClinicianId
        : null;
    const preferredClinician =
      !_.isEmpty(this.personaliseTreatment) && !_.isEmpty(this.personaliseTreatment.state.selectedPreferredClinician)
        ? this.personaliseTreatment.state.selectedPreferredClinician
        : null;

    const treatmentDraft = {
      ...this.treatmentForm.getModel(),
      ...this.treatmentScheduleForm.getModel(),
      address,
      visitType,
      overriddenRagStatusId,
      overriddenBandId,
      preferredClinicianId,
      preferredClinician,
    };

    const activitiesIds = treatmentDraft.activitiesContainer.map(v => v.id);
    const activityVariancesIds = treatmentDraft.activitiesContainer.filter(v => v.innerId !== null).map(v => v.innerId);

    treatmentDraft.activitiesIds = activitiesIds;
    treatmentDraft.activityVariancesIds = activityVariancesIds;
    if (!treatmentDraft.hasAdditionalClinician) {
      treatmentDraft.additionalClinician = 0;
    }

    if (isSpecific && treatmentDraft.recurrenceType === recurrenceTypes.DAILY) {
      treatmentDraft.selectedDays = {};
    }
    const { dateFrom, dateTo, reviewDate, specificTimeFrom, recurEvery = 1 } = treatmentDraft;

    const phoneCallContact = this.state.patientContact;

    phoneCallContact.patientPasKey = patient ? patient.id : '';
    phoneCallContact.isModified =
      patient &&
      (patient.contact.homeTelephone !== phoneCallContact.landlinePhoneNumber ||
        patient.contact.workTelephone !== phoneCallContact.otherPhoneNumber ||
        patient.contact.mobileTelephone !== phoneCallContact.mobilePhoneNumber);

    saveCallback({
      ...treatmentDraft,
      specificTimeFrom: getDateForApi(specificTimeFrom),
      dateFrom: getDateForApi(dateFrom),
      dateTo: getDateForApi(dateTo),
      reviewDate: getDateForApi(reviewDate),
      recurEvery,
      isSpecific,
      isMultiple,
      phoneCallContact,
    });
  };

  canSave = () => {
    const formsValidity = _.values(this.state.formsValidity);
    return formsValidity.every(el => el === true);
  };

  handleClear = () => {
    const { selectedTab } = this.state;
    const { treatmentDetails } = this.props;
    const tabName = tabIndexToNameMap[selectedTab];
    let resetObject = null;
    if (selectedTab === 1) {
      this.treatmentScheduleWrapper.reset();
    } else if (selectedTab === 0 && treatmentDetails) {
      resetObject = {};
      if (treatmentDetails.activitiesIds) {
        resetObject.activitiesContainer = treatmentDetails.activitiesIds.map(a => a);
        this.setState({
          wholeFormErrorMessage: null,
          isReset: true,
          activitiesIds: resetObject.activitiesContainer,
        });
      }
      if (treatmentDetails.orgUnitIds) {
        const oldValue = treatmentDetails.orgUnitIds[0].id;
        resetObject.orgUnitIds = oldValue;
      }
      if (treatmentDetails.weeklyRequestedDays) {
        resetObject.weeklyRequestedDays = treatmentDetails.weeklyRequestedDays;
      }
    } else if (selectedTab === 3) {
      this.personaliseTreatment.reset();
    }
    this[`${tabName}Form`].reset(resetObject);
  };

  handleTabChange = (unused, event, tab) => {
    this.setState({
      selectedTab: tab.props.index,
    });
  };

  patternValidHeader = () => {
    const { actionType, validFrom, validTo } = this.props;
    const headerStyle = {
      marginTop: '16px',
    };
    if (actionType === actionTypes.edit && validFrom && validTo) {
      return (
        <Typography variant="h4" style={headerStyle}>
          {locale.getMsg('patternValidHeader', [
            moment(validFrom).format(globals.dateFormats.short),
            moment(validTo).format(globals.dateFormats.short),
          ])}
        </Typography>
      );
    }
    return null;
  };

  render() {
    const { wholeFormErrorMessage, patientContact } = this.state;
    const {
      readOnly,
      treatmentDetails,
      treatmentAddress,
      patientAddress,
      timeSlots,
      treatmentSchedule,
      isSpecificTime,
      isMultiple,
      actionType,
      timeSlotsPackageId,
      description,
      showErrorMessage,
      hasBundledOrFutureAssignedInstances,
      referralDateReceived,
      referralServiceEndDate,
      treatment,
      handleDiscard,
      jwtToken,
      enableIntegrationMode,
      externalProviderType,
      isSaveDisabled,
      enableRecordingOfWeeklyRequestedDays,
      enableMappingOfTeams,
    } = this.props;

    const canBeDiscarded = treatment && treatment.id > 0 && treatment.canBeDiscarded;
    const isImportedFromSystmOne =
      enableIntegrationMode &&
      treatment &&
      treatment.isImported &&
      externalProviderType === ExternalProviderType.SystmOne;

    const shouldBeVisible = () => {
      return !isImportedFromSystmOne || this.state.selectedTab === 3;
    };

    const shouldRenderFooterButtons = () => {
      return !this.props.readOnly && this.state.selectedTab !== 4;
    };

    return (
      <React.Fragment>
        <Box display="flex" px={1} flexDirection="column">
          <Box p={1} flexGrow={1}>
            <Typography variant="h6">{locale.getMsg(`${actionType.toLowerCase()}ReferralTreatment`)}</Typography>
            <Typography variant="subtitle1">
              {!readOnly && <p>{locale.getMsg('pleaseCompleteDescription')}</p>}
            </Typography>
          </Box>
          <Box p={1} flexGrow={1}>
            <Tabs
              tabItemContainerStyle={tabStyles.tabItemContainerStyle}
              inkBarStyle={tabStyles.inkBarStyle}
              initialSelectedIndex={0}
              onChange={this.handleTabChange}
            >
              <Tab buttonStyle={tabStyles.buttonStyle} label={locale.getMsg('details')}>
                <TreatmentDetails
                  readOnly={readOnly}
                  innerRef={this.setRef('treatmentForm')}
                  setValidity={this.setValidity('treatment')}
                  isReset={this.state.isReset}
                  treatmentDetails={treatmentDetails}
                  actionType={actionType}
                  description={description}
                  hasBundledOrFutureAssignedInstances={hasBundledOrFutureAssignedInstances}
                  handleCareTypeChange={this.handleCareTypeChange}
                  handleActivityChange={this.handleActivityChange}
                  selectedCareType={this.state.treatmentCareType}
                  jwtToken={jwtToken}
                  isImportedFromSystmOne={isImportedFromSystmOne}
                  enableRecordingOfWeeklyRequestedDays={
                    enableRecordingOfWeeklyRequestedDays && (!treatment || !treatment.isAddedRetrospectively)
                  }
                  enableMappingOfTeams={enableMappingOfTeams}
                />
              </Tab>
              <Tab buttonStyle={tabStyles.buttonStyle} label={locale.getMsg('treatmentSchedule')}>
                {this.patternValidHeader()}
                <TreatmentSchedule
                  readOnly={readOnly}
                  innerRef={this.setRef('treatmentScheduleForm')}
                  setValidity={this.setValidity('treatmentSchedule')}
                  ref={this.setRef('treatmentScheduleWrapper')}
                  timeSlots={timeSlots}
                  timeSlotsPackageId={timeSlotsPackageId}
                  treatmentSchedule={treatmentSchedule}
                  isSpecificTime={isSpecificTime}
                  isMultiple={isMultiple}
                  actionType={actionType}
                  hasBundledOrFutureAssignedInstances={hasBundledOrFutureAssignedInstances}
                  referralDateReceived={referralDateReceived}
                  referralServiceEndDate={referralServiceEndDate}
                  isImportedFromSystmOne={isImportedFromSystmOne}
                  treatmentId={(treatment && treatment.id) || -1}
                />
              </Tab>
              <Tab label={locale.getMsg('addressTab')} buttonStyle={tabStyles.buttonStyle}>
                {this.state.treatmentCareType === 'OnSite' && (
                  <AddressDetails
                    readOnly={readOnly}
                    treatmentAddress={treatmentAddress}
                    patientAddress={patientAddress}
                    innerRef={this.setRef('addressContactForm')}
                    setValidity={this.setValidity('addressContact')}
                    actionType={actionType}
                    hasBundledOrFutureAssignedInstances={hasBundledOrFutureAssignedInstances}
                    hasAlternativeAddres={this.hasAlternativeAddres}
                    isImportedFromSystmOne={isImportedFromSystmOne}
                  />
                )}
                {this.state.treatmentCareType === 'PhoneCall' && (
                  <ContactDetails
                    readOnly={readOnly}
                    patientContact={patientContact}
                    innerRef={this.setRef('addressContactForm')}
                    setValidity={this.setValidity('addressContact')}
                    getPatientPhoneNumbers={this.getPatientPhoneNumbers}
                  />
                )}
              </Tab>
              {actionType === actionTypes.edit && !treatment.isAddedRetrospectively && (
                <Tab buttonStyle={tabStyles.buttonStyle} label={locale.getMsg('personaliseTreatment')}>
                  <PersonaliseTreatment
                    readOnly={readOnly}
                    ref={this.setRef('personaliseTreatment')}
                    innerRef={this.setRef('personaliseTreatmentForm')}
                    setValidity={this.setValidity('personaliseTreatment')}
                    activities={this.state.activitiesIds}
                    treatment={treatment}
                    treatmentDraft={this.treatmentDraft}
                    patientFullName={this.props.patient.patientFullName}
                    showErrorMessage={showErrorMessage}
                  />
                </Tab>
              )}
              {actionType === actionTypes.edit && !treatment.isAddedRetrospectively && (
                <Tab buttonStyle={tabStyles.buttonStyle} label={'Audit Trail'}>
                  <AuditTrail auditTrailType={AuditTrailType.Treatment} entityId={treatment.id} />
                </Tab>
              )}
            </Tabs>
          </Box>
        </Box>
        {shouldRenderFooterButtons() && (
          <Box display="flex" p={1}>
            {shouldBeVisible() && (
              <Box p={1}>
                <FlatButton label={locale.getMsg('clearTab')} onClick={this.handleClear} />
              </Box>
            )}
            {canBeDiscarded && (
              <Box p={1}>
                <RaisedButton
                  label={locale.getMsg('discardTreatment')}
                  backgroundColor={red500}
                  onClick={handleDiscard}
                  disabled={isImportedFromSystmOne}
                />
              </Box>
            )}
            <Box p={1}>
              <RaisedButton
                type="submit"
                label={locale.getMsg('save')}
                onClick={this.handleSave}
                primary
                disabled={(isImportedFromSystmOne && this.state.selectedTab !== 3) || isSaveDisabled}
              />
              {wholeFormErrorMessage && wholeFormErrorMessage.length ? (
                <span style={{ color: 'red', margin: '10px 0 0 10px' }}>{wholeFormErrorMessage}</span>
              ) : (
                showErrorMessage && (
                  <span style={{ color: 'red', margin: '10px 0 0 10px' }}>
                    {locale.getMsg('timeslotDurationError')}
                  </span>
                )
              )}
            </Box>
          </Box>
        )}
      </React.Fragment>
    );
  }
}
TreatmentPreview.defaultProps = {
  saveCallback: () => {},
  handleDiscard: () => {},
  readOnly: false,
  patientAddress: {},
  treatmentAddress: {},
  patient: null,
  treatmentDetails: {
    communityMatron: false,
    emergencyUnscheduled: false,
    orgUnitIds: undefined,
    activitiesIds: undefined,
    reviewDate: undefined,
    weeklyRequestedDays: null,
  },
  validFrom: null,
  validTo: null,
  timeSlots: [],
  treatmentSchedule: {
    recurEvery: 1,
    recurrenceType: recurrenceTypes.DAILY,
    dateTo: undefined,
    dateFrom: undefined,
    timeSlotsMatrix: undefined,
    timeSlotsPackage: undefined,
    monthlyRecurrenceType: undefined,
    selectedWeeks: undefined,
    selectedWeekDays: undefined,
  },
  isSpecificTime: false,
  showErrorMessage: false,
  isMultiple: false,
  timeSlotsPackageId: null,
  description: '',
  hasBundledOrFutureAssignedInstances: false,
  isSaveDisabled: false,
};

TreatmentPreview.propTypes = {
  patientAddress: PropTypes.shape({
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    addressLine3: PropTypes.string,
    postCode: PropTypes.string,
  }),
  patient: PatientPropType,
  treatmentAddress: PropTypes.shape({
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    addressLine3: PropTypes.string,
    postCode: PropTypes.string,
  }),
  treatmentDetails: PropTypes.shape({
    teams: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    activities: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    reviewDate: PropTypes.string,
    communityMatron: PropTypes.bool,
    emergencyUnscheduled: PropTypes.bool,
    weeklyRequestedDays: PropTypes.number,
  }),
  validFrom: PropTypes.string,
  validTo: PropTypes.string,
  readOnly: PropTypes.bool,
  saveCallback: PropTypes.func,
  handleDiscard: PropTypes.func,
  timeSlots: PropTypes.arrayOf(timeSlotPropType),
  timeSlotsPackageId: PropTypes.number,
  treatmentSchedule: PropTypes.shape({
    recurEvery: PropTypes.number,
    recurrencePattern: PropTypes.string,
    dateTo: PropTypes.string,
    dateFrom: PropTypes.string,
    monthlyRecurrenceType: PropTypes.string,
    selectedWeeks: PropTypes.arrayOf(PropTypes.string),
    selectedWeekDays: PropTypes.arrayOf(PropTypes.string),
    timeSlotsMatrix: PropTypes.objectOf(PropTypes.objectOf(PropTypes.bool)),
    timeSlotsPackage: PropTypes.shape({
      id: PropTypes.number,
      timeSlots: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          from: PropTypes.string,
          to: PropTypes.string,
          name: PropTypes.string,
        }),
      ),
    }),
  }),
  isSpecificTime: PropTypes.bool,
  showErrorMessage: PropTypes.bool,
  isMultiple: PropTypes.bool,
  actionType: PropTypes.string.isRequired,
  description: PropTypes.string,
  hasBundledOrFutureAssignedInstances: PropTypes.bool,
  isSaveDisabled: PropTypes.bool,
};

export default TreatmentPreview;
